import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { API_BASE_URL } from '../api';

const CHANNEL_OPTIONS = [
  { value: '', label: 'All channels' },
  { value: 'estimate_share_link', label: 'Estimate share link' },
];

function shortClass(t) {
  if (!t) return '—';
  const parts = String(t).split('\\');
  return parts[parts.length - 1] || t;
}

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function CommunicationLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [channel, setChannel] = useState('');
  const [subjectIdInput, setSubjectIdInput] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = subjectIdInput.trim();
      setSubjectId(/^\d+$/.test(trimmed) ? trimmed : '');
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [subjectIdInput]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const qs = new URLSearchParams({ page: String(page), per_page: '25' });
        if (channel) qs.set('channel', channel);
        if (subjectId) qs.set('subject_id', subjectId);
        const res = await fetch(`${API_BASE_URL}communication-logs?${qs}`, {
          headers: {
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || data.message || `HTTP ${res.status}`);
        }
        if (cancelled) return;
        setRows(Array.isArray(data?.data) ? data.data : []);
        const lp = data?.last_page;
        setLastPage(typeof lp === 'number' && lp >= 1 ? lp : 1);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load communication log');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, channel, subjectId]);

  return (
    <Box sx={{ width: '100%', maxWidth: 1280, mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <ChatBubbleOutlineIcon color="primary" fontSize="large" />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Message log
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Outbound actions logged from the app (e.g. estimate share links). More channels can be added later.
      </Typography>

      <Paper sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="comm-channel-label">Channel</InputLabel>
          <Select
            labelId="comm-channel-label"
            label="Channel"
            value={channel}
            onChange={(e) => {
              setChannel(e.target.value);
              setPage(1);
            }}
          >
            {CHANNEL_OPTIONS.map((o) => (
              <MenuItem key={o.value || 'all'} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Estimate ID (subject)"
          placeholder="e.g. 12"
          value={subjectIdInput}
          onChange={(e) => setSubjectIdInput(e.target.value)}
          sx={{ width: 180 }}
        />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Channel</TableCell>
                  <TableCell>To / summary</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell align="right">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No entries yet. Share an estimate link to create a log row.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{formatWhen(r.created_at)}</TableCell>
                      <TableCell>{r.channel}</TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>{r.to_summary || '—'}</TableCell>
                      <TableCell>{r.user?.name || r.user?.email || '—'}</TableCell>
                      <TableCell>
                        {shortClass(r.subject_type)} #{r.subject_id ?? '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" sx={{ textTransform: 'none' }} onClick={() => { setSelected(r); setDetailsOpen(true); }}>
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {lastPage > 1 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination page={page} count={lastPage} onChange={(e, p) => setPage(p)} color="primary" />
            </Box>
          ) : null}
        </>
      )}

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Log entry #{selected?.id}</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(
                {
                  channel: selected.channel,
                  created_at: selected.created_at,
                  to_summary: selected.to_summary,
                  subject_type: selected.subject_type,
                  subject_id: selected.subject_id,
                  user: selected.user,
                  meta: selected.meta,
                },
                null,
                2
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
